/**
 * Dexie schema.
 *
 * Every row is an envelope: a small set of clear-text index columns plus an
 * AES-GCM sealed payload holding everything identifying. The clear columns are
 * deliberately non-identifying — UUIDs, month buckets, status flags — so that
 * an attacker with raw IndexedDB access learns how many trips exist and when,
 * but not who was carried or where.
 *
 * The cost of that choice is that we cannot index on a passenger name. In
 * practice this does not matter: the working set is one operator's trips, the
 * suggestion index is built in memory at unlock, and period exports select by
 * month bucket.
 *
 * Each account gets its own IndexedDB database rather than sharing tables
 * behind an owner column. Three things here are per-account counters read off
 * the clear index — the audit chain's `seq`, the invoice number circle, and the
 * order book's `Lfd. Nr.` — and every one of them has to be gapless for its
 * owner alone. Sharing tables would mean scoping every query correctly forever,
 * where forgetting once yields either a cross-account leak or a duplicate
 * invoice number. Separate databases make the browser enforce what would
 * otherwise be a convention.
 */

import Dexie, { type Table } from 'dexie';
import type { Sealed } from '@/crypto/primitives';
import type { AccountRecord } from '@/crypto/vault';

export const DB_NAME = 'fahrtenbuch-pro';
/**
 * 2 — the order table gained the `Lfd. Nr.` index of the paper order book.
 * 3 — accounts moved out to the registry, so this database holds one driver's
 *     books and nothing else.
 */
export const DB_VERSION = 3;

/**
 * The registry of accounts, shared by every driver on the device.
 *
 * It has to be readable before anyone has signed in — the login screen lists
 * the drivers it offers — so it is separate from the encrypted books and holds
 * only credential material. Nothing in it is personal beyond a display name.
 */
export const ACCOUNTS_DB_NAME = 'fahrtenbuch-pro-accounts';

/** Where one account's books live. */
export function accountDbName(accountId: string): string {
  return `${DB_NAME}__${accountId}`;
}

/** The sealed-envelope shape shared by every domain table. */
export interface Envelope {
  id: string;
  /** `YYYY-MM`, or `static` for tables without a natural time axis. */
  bucket: string;
  iv: string;
  ct: string;
  /** Payload schema version, so a future migration knows what it is reading. */
  pv: number;
}

export const STATIC_BUCKET = 'static';
export const PAYLOAD_VERSION = 1;

/** Trips carry extra clear columns needed for cheap joins and lock checks. */
export interface TripEnvelope extends Envelope {
  shiftId: string;
  invoiceId: string | null;
  /** Clear so the lock sweep does not have to decrypt the whole table. */
  date: string;
  lockedAt: string | null;
}

export interface ShiftEnvelope extends Envelope {
  date: string;
  closedAt: string | null;
}

export interface OrderEnvelope extends Envelope {
  tripId: string | null;
  /**
   * `Lfd. Nr.` and its year, in clear. Handing out the next number must not
   * require decrypting a year of orders, and a gap in the sequence has to be
   * detectable by counting rows — which is the whole point of the number.
   */
  sequenceNumber: number;
  year: string;
}

export interface InvoiceEnvelope extends Envelope {
  invoiceNumber: string;
  numberCircle: string;
  status: string;
  issueDate: string;
}

/**
 * Audit entries keep their linkage fields in clear so the chain can be
 * verified for structural integrity without unlocking, and so a gap in `seq`
 * is visible even to a forensic examiner who has no password.
 */
export interface AuditEnvelope extends Envelope {
  seq: number;
  prevHash: string;
  hash: string;
  at: string;
  entityType: string;
  entityId: string;
}

/** Small unencrypted operational values. Never holds personal data. */
export interface MetaRow {
  key: string;
  value: unknown;
}

/** The device-wide account registry. Holds no books and no trip data. */
export class AccountsDb extends Dexie {
  accounts!: Table<AccountRecord, string>;
  meta!: Table<MetaRow, string>;

  constructor(name: string = ACCOUNTS_DB_NAME) {
    super(name);
    this.version(1).stores({
      accounts: 'id, username',
      meta: 'key',
    });
  }
}

export const accountsDb = new AccountsDb();

export class FahrtenbuchDb extends Dexie {
  trips!: Table<TripEnvelope, string>;
  shifts!: Table<ShiftEnvelope, string>;
  orders!: Table<OrderEnvelope, string>;
  passengers!: Table<Envelope, string>;
  addresses!: Table<Envelope, string>;
  vehicles!: Table<Envelope, string>;
  invoices!: Table<InvoiceEnvelope, string>;
  settings!: Table<Envelope, string>;
  auditLog!: Table<AuditEnvelope, number>;

  constructor(name: string) {
    super(name);

    // Version 2 is declared so an install predating the split upgrades cleanly
    // rather than tripping Dexie's schema check.
    this.version(2).stores({
      accounts: 'id, username',
      meta: 'key',

      trips: 'id, bucket, shiftId, invoiceId, date, lockedAt',
      shifts: 'id, bucket, date, closedAt',
      orders: 'id, bucket, tripId, year, [year+sequenceNumber]',
      passengers: 'id, bucket',
      addresses: 'id, bucket',
      vehicles: 'id, bucket',
      invoices: 'id, bucket, invoiceNumber, numberCircle, status, issueDate',
      settings: 'id',

      // `seq` is the primary key: monotonic, gapless, and the thing an auditor
      // counts. `++seq` would let Dexie reuse numbers after a delete, so the
      // sequence is assigned explicitly by the audit writer instead.
      auditLog: 'seq, hash, at, entityType, entityId',
    });

    // The accounts have been copied into the registry by the time this runs,
    // so the local copies go. `null` is Dexie's instruction to drop a store.
    this.version(DB_VERSION).stores({ accounts: null, meta: null });
  }
}

/**
 * The books of whoever is currently signed in.
 *
 * Re-pointed at unlock and dropped at lock. This is a live binding: importers
 * see the reassignment, which is what lets every repository and query below
 * stay unaware that more than one driver exists. The one thing that does not
 * work is capturing a table at module scope — see `SealedRepository`, whose
 * tables are resolved per call for exactly this reason.
 */
export let db: FahrtenbuchDb = new FahrtenbuchDb(DB_NAME);

/**
 * Whether `db` has been closed and needs replacing before it is used again.
 *
 * Tracked here rather than asked of Dexie: `hasBeenClosed()` in 4.4.5 tests
 * the open error against `DatabaseClosed`, but the error it stores is named
 * `DatabaseClosedError`, so it answers no to a database it has just closed.
 */
let closed = false;

/**
 * Point `db` at one account's books.
 *
 * Dexie opens lazily, so constructing the handle costs nothing until the first
 * query. Returning early on a repeat call keeps an unlock idempotent — but
 * only while the handle is still usable: a closed Dexie keeps its name and
 * throws on every query, so a driver who locks and signs straight back in
 * matches on name alone and would otherwise be handed the dead handle.
 */
export function openAccountDb(dbName: string): FahrtenbuchDb {
  if (db.name === dbName && !closed) return db;
  db.close();
  db = new FahrtenbuchDb(dbName);
  closed = false;
  return db;
}

/** Drop the handle at lock, so a later unlock cannot reuse a stale one. */
export function closeAccountDb(): void {
  db.close();
  closed = true;
}

export function monthBucket(isoDate: string): string {
  return isoDate.slice(0, 7);
}

/** Inclusive list of `YYYY-MM` buckets spanning two dates. */
export function bucketRange(fromIso: string, toIso: string): string[] {
  const buckets: string[] = [];
  const start = new Date(`${fromIso.slice(0, 7)}-01T00:00:00Z`);
  const end = new Date(`${toIso.slice(0, 7)}-01T00:00:00Z`);
  const cursor = new Date(start);
  while (cursor <= end) {
    buckets.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return buckets;
}

export type { Sealed };
